import { PageDataTable } from "components/PageDataTable/PageDataTable";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useNavigate } from "react-router-dom";
import { useGetSftpPartnersQuery } from "store/queries/sftpPartners";
import { ISftpPartnerSummary } from "types/SftpPartner";

import { formatDateTime } from "../../utils/hstImports";
import {
  ingestKindLabel,
  ingestModeLabel,
  relativeTime,
  SFTP_NEW_PARTNER_PATH,
  sftpPartnerPath,
} from "../../utils/sftpPartners";
import { SftpAccountName } from "../SftpPartnerParts";

export function SftpStatusTag({ enabled }: { enabled: boolean }) {
  return enabled ? (
    <Tag value="Enabled" severity="success" />
  ) : (
    <Tag value="Disabled" style={{ background: "var(--gray-500)" }} />
  );
}

const slugBody = (p: ISftpPartnerSummary) => (
  <span className="font-semibold">{p.slug}</span>
);

const accountBody = (p: ISftpPartnerSummary) => (
  <SftpAccountName accountId={p.accountId} accountName={p.accountName} />
);

const modeBody = (p: ISftpPartnerSummary) => ingestModeLabel(p.ingestMode);

const kindBody = (p: ISftpPartnerSummary) => ingestKindLabel(p.ingestKind);

// Mapping rules only exist for EDI partners.
const rulesBody = (p: ISftpPartnerSummary) =>
  p.ingestKind === "EDI" ? p.ruleCount : "—";

const outboundBody = (p: ISftpPartnerSummary) =>
  p.outboundReportCount ? p.outboundReportCount : "—";

const statusBody = (p: ISftpPartnerSummary) => (
  <SftpStatusTag enabled={p.enabled} />
);

const lastProcessedBody = (p: ISftpPartnerSummary) =>
  p.lastProcessedAt ? (
    <span title={formatDateTime(p.lastProcessedAt)}>
      {relativeTime(p.lastProcessedAt)}
    </span>
  ) : (
    <span className="text-600">Never</span>
  );

const prefixBody = (p: ISftpPartnerSummary) => <code>{p.s3Prefix}</code>;

// Partners with a folder in s3://clearhealth-sftp/<slug>/: their inbound files (EDI, Athena orders or
// none — the Kind column) and the reports we write into the folder (Outgoing reports).
// A row opens the partner's page (edit, enable / disable); "New partner" opens the create form.
export function SftpPartnersTab() {
  const navigate = useNavigate();
  const { data, isFetching, isError } = useGetSftpPartnersQuery();
  const partners = data?.data || [];

  return (
    <div className="flex flex-column gap-3">
      <div className="flex justify-content-end">
        <Button
          label="New partner"
          icon="pi pi-plus"
          onClick={() => navigate(SFTP_NEW_PARTNER_PATH)}
          data-testid="sftp-new-partner"
        />
      </div>
      {isError ? (
        <Message
          severity="error"
          text="Couldn't load SFTP partners. Refresh to try again."
        />
      ) : null}
      <PageDataTable
        value={partners}
        dataKey="slug"
        loading={isFetching}
        emptyMessage="No SFTP partners configured."
        onRowClick={(e) =>
          navigate(sftpPartnerPath((e.data as ISftpPartnerSummary).slug))
        }
        rowClassName={() => "cursor-pointer"}
      >
        <Column header="Partner" body={slugBody} />
        <Column header="Account" body={accountBody} />
        <Column header="Mode" body={modeBody} />
        <Column header="Kind" body={kindBody} />
        <Column header="Status" body={statusBody} />
        <Column header="Rules" body={rulesBody} />
        <Column header="Files processed" field="filesProcessed" />
        <Column header="Outgoing reports" body={outboundBody} />
        <Column header="Last file processed" body={lastProcessedBody} />
        <Column header="S3 prefix" body={prefixBody} />
      </PageDataTable>
    </div>
  );
}
